import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router';

import { useMediaQuery } from '@mui/material';

import usePageMeta from 'hooks/use-page-meta';
import useStores from 'hooks/use-stores';
import SearchTemplates from 'main/components/search/SearchTemplates';
import { SearchType } from 'main/components/SearchBar';
import { ElasticQueryBuilder } from 'main/modules/elastic';
import isomorphicLocalStorage from 'main/modules/isomorphic-local-storage';
import Landing from 'pages/Landing';
import Advertising from 'pages/Search/components/Database/components/Advertising';
import BulletinTypes from 'pages/Search/components/Database/components/BulletinTypes';
import Layout from 'pages/Search/components/Layout';
import isEmpty from 'utils/is-empty';
import { stringifyQuery } from 'utils/stringify-query';
import Utils from 'utils/Utils';

import ElasticFilter from './components/ElasticFilter';
import Filter from './components/Filter';
import SearchResults from './components/SearchResults';
import Widgets from './components/Widgets';
import useStyles from './styles';

const Database = () => {
    const { classes } = useStyles();
    const history = useHistory();

    const { naslStore, statsStore, searchStore, antiBotStore } = useStores();
    const search = searchStore.query || searchStore.software;
    usePageMeta({
        title: search
            ? 'Vulners Database - ' + Utils.trunc(100, search)
            : 'CVE Search Engine - Security Vulnerabilities and Exploits Search Tool',
        description: 'Vulners - Vulnerability DataBase'
    });

    const layoutRef = useRef<{ scrollTop: () => void }>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();

    const sendGA = useCallback(() => {
        clearTimeout(timerRef.current);
        try {
            window.ga('set', {
                page: window.location.pathname + window.location.search,
                location: window.location.href,
                title: 'Search'
            });
            window.ga('send', 'pageview');
        } catch (e) {
            timerRef.current = setTimeout(() => sendGA(), 1000);
        }
    }, []);

    const onPageChange = useCallback(
        ({ query, software, version, state, skip, size }) => {
            history.push(
                stringifyQuery('/search', {
                    query,
                    software,
                    version,
                    state: ElasticQueryBuilder.stateToString(state),
                    skip,
                    size
                })
            );
            layoutRef.current?.scrollTop();
            searchStore.setParameters({
                query: searchStore.type === SearchType.elastic ? state.searchTerm ?? '' : query ?? '',
                software,
                version,
                state,
                type: searchStore.type
            });
            searchStore.setPageSize(size);
            searchStore.performFetch({ skip });
            antiBotStore.randomCollectData();
            sendGA();
        },
        [antiBotStore, history, searchStore, sendGA]
    );

    const refetch = useCallback(() => {
        const url = new URLSearchParams(window.location.search);
        const query = url.get('query')?.trim() || '';
        const software = url.get('software')?.trim() || '';
        const version = url.get('version')?.trim() || '';
        const skip = url.get('skip')?.trim();
        const size = url.get('size')?.trim();
        const state = url.get('state')?.trim() || '';

        let type = searchStore.type;

        let queryParameter = query;
        let stateParameter = {};

        switch (true) {
            case !!state: {
                type = SearchType.elastic;
                const parsed = ElasticQueryBuilder.stringToState(state);
                stateParameter = parsed;
                queryParameter = parsed.searchTerm ?? '';
                break;
            }
            case !!query && !software:
                type = SearchType.simple;
                break;
            case !query && !!software:
                type = SearchType.product;
                break;
            default:
                break;
        }

        searchStore.setParameters({
            query: queryParameter,
            software,
            version,
            type,
            state: stateParameter
        });

        if (size) {
            searchStore.pageSize = Number(size);
        }

        searchStore.performFetch({ skip });
    }, [searchStore]);

    useEffect(() => {
        const unsubscribe = history.listen((_, action) => {
            if (action === 'POP') {
                refetch();
            }
        });
        return () => {
            unsubscribe();
            searchStore.resetParameters();
            clearTimeout(timerRef.current);
        };
    }, [history, refetch, searchStore]);

    useEffect(() => {
        naslStore.loadTypes();
        statsStore.loadStats();
        statsStore.loadBulletinStats();

        refetch();

        if (isomorphicLocalStorage.getItem(Landing.LS_LANDING_VISITED) === 'true') {
            isomorphicLocalStorage.setItem(Landing.LS_LANDING_SHOW, 'false');
        }

        sendGA();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const shouldShowSearch = !isEmpty(searchStore.query) || !isEmpty(searchStore.software) || searchStore.hasTerms;

    const shouldShowFilter =
        useMediaQuery('(min-width:1024px)') &&
        shouldShowSearch &&
        [SearchType.simple, SearchType.elastic].includes(searchStore.type);

    return (
        <Layout
            ref={layoutRef}
            main={
                <>
                    <SearchTemplates />
                    {shouldShowSearch ? (
                        <SearchResults onPageChange={onPageChange} />
                    ) : (
                        <div className={classes.wrapper}>
                            <Widgets />
                            <BulletinTypes />
                            <Advertising />
                        </div>
                    )}
                </>
            }
            additional={
                shouldShowFilter ? searchStore.type === SearchType.elastic ? <ElasticFilter /> : <Filter /> : null
            }
        />
    );
};

export default observer(Database);
